import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  WeeklyGanttDataResponse,
  GanttData,
  GanttDataEntry,
  DriverRow,
  DriverTime,
} from 'src/app/models/gantt/gantt.model';

import * as dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { EMPTY_GUID, formatSecondsToHourAndMinutes } from 'src/app/utils';
import { BsModalService } from 'ngx-bootstrap/modal';
import { GanttWeeklyModalComponent } from './components/gantt-weekly-modal/gantt-weekly-modal.component';
import { DriverType, DriverTypeLabel } from 'src/app/enum/driver-type.enum';
import { VerticalType } from 'src/app/enum/vertical-type/verticalType.enum';

@Component({
  selector: 'app-gantt-weekly',
  templateUrl: './gantt-weekly.component.html',
  standalone: false,
  styleUrls: ['./gantt-weekly.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GanttWeeklyComponent implements OnChanges, OnInit, OnDestroy {
  /** Weekly response coming from the service */
  @Input() data!: WeeklyGanttDataResponse;
  @Input() weekStart!: Date;
  @Output() updateGantt = new EventEmitter<boolean>();
  days: { label: string; date: dayjs.Dayjs }[] = [];
  rows: DriverRow[] = [];

  private destroy$ = new Subject<void>();
  emptyGuid: string = EMPTY_GUID;
  private localeReady = false;
  public driverTypeLabel = DriverTypeLabel;

  constructor(
    private translate: TranslateService,
    private modalService: BsModalService
  ) {}

  ngOnInit(): void {
    this.setDayjsLocale(this.translate.currentLang);
    this.localeReady = true;

    if (this.weekStart) {
      this.buildWeekSkeleton();
      if (this.data) this.mapTasks();
    }

    (this.translate.onLangChange as Observable<LangChangeEvent>)
      .pipe(takeUntil(this.destroy$))
      .subscribe((e) => {
        this.setDayjsLocale(e.lang);
        this.buildWeekSkeleton();
      });
  }

  private setDayjsLocale(lang: string): void {
    dayjs.locale(lang.split('-')[0]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.localeReady) return;

    if (changes['weekStart']) {
      this.buildWeekSkeleton();
    }
    if (changes['data'] && this.data) {
      this.mapTasks();
    }
  }

  openTaskDetailsModal(task: GanttData, row: DriverRow): void {
    const modalRef = this.modalService.show(GanttWeeklyModalComponent, {
      class: 'gray modal-dialog-centered mw-500 br-12',
      ignoreBackdropClick: false,
      initialState: {
        taskGanttData: task,
        driverRow: row,
      },
    });

    (modalRef.content as GanttWeeklyModalComponent).updateGantt.subscribe(
      () => {
        this.updateGantt.emit(true);
      }
    );
  }

  getStatusStyle(task: GanttData): string {
    if (!task) return 'gantt-task-confirmed';

    /* ── 1. Por tipo de nodo ───────────────────────────── */
    const nodeClass: Record<string, string> = {
      TRANSPORT_NODE: 'gantt-task-public',
      SWEEPER_NODE: 'gantt-task-sweeper',
    };
    if (nodeClass[task.nodeType]) {
      return nodeClass[task.nodeType];
    }

    /* ── 2. Por estado del pedido ──────────────────────── */
    const status = String(task.orderStatus).toUpperCase();

    const statusClass: Record<string, string> = {
      // Pendiente
      '1': 'gantt-task-pending',
      PENDING: 'gantt-task-pending',

      // Confirmado
      '2': 'gantt-task-confirmed',
      CONFIRMED: 'gantt-task-confirmed',

      // En curso
      '3': 'gantt-task-inProgress',
      '4': 'gantt-task-inProgress',
      '5': 'gantt-task-inProgress',
      PICKING_UP: 'gantt-task-inProgress',
      IN_PROGRESS: 'gantt-task-inProgress',
      DELIVERING: 'gantt-task-inProgress',

      // Finalizado
      '6': 'gantt-task-finished',
      FINISHED: 'gantt-task-finished',

      // Cancelado / fallido
      '7': 'gantt-task-cancelled',
      '10': 'gantt-task-cancelled',
      '11': 'gantt-task-cancelled',
      FAILED: 'gantt-task-cancelled',
      CANCELLED_BY_CAFLER: 'gantt-task-cancelled',
      CANCELLED_BY_CLIENT: 'gantt-task-cancelled',
    };

    return statusClass[status] ?? 'gantt-task-confirmed';
  }

  getIcon(nodeType: string, verticalType: VerticalType): string {
    // Primero, manejamos los nodos sweeper y transport
    if (nodeType === 'SWEEPER_NODE') {
      return '../../../assets/icons/gantt-icons/Type=initial_trans.svg';
    }
    if (nodeType === 'TRANSPORT_NODE') {
      return '../../../assets/icons/gantt-icons/Type=public_trans.svg';
    }

    // Si llegamos aquí, asumimos que es OPERATIVE_NODE
    switch (verticalType) {
      case VerticalType.Transfer:
        return '../../../assets/icons/gantt-icons/Type=Transfer.svg';
      case VerticalType.MechanicalInspection:
        return '../../../assets/icons/gantt-icons/Type=Falta.svg';
      case VerticalType.VehicleWash:
        return '../../../assets/icons/gantt-icons/Type=Lavados.svg';
      case VerticalType.Refueling:
        return '../../../assets/icons/gantt-icons/Type=Repostaje.svg';
      case VerticalType.PreTechnicalInspection:
        return '../../../assets/icons/gantt-icons/Type=Pre_ITV.svg';
      case VerticalType.LongDistanceTransfer:
        return '../../../assets/icons/gantt-icons/Type=Media_y_larga_distancia.svg';
      case VerticalType.TechnicalInspection:
        return '../../../assets/icons/gantt-icons/Type=ITV.svg';
      case VerticalType.VehicleInsurance:
        return '../../../assets/icons/gantt-icons/Type=Seguros.svg';
      case VerticalType.Accessory:
        return '../../../assets/icons/gantt-icons/Type=Accesorio.svg';
      case VerticalType.Valet:
        return '../../../assets/icons/gantt-icons/Type=Valet.svg';
      case VerticalType.Tyres:
        return '../../../assets/icons/gantt-icons/Type=Neumaticos.svg';
      case VerticalType.Formalities:
        return '../../../assets/icons/gantt-icons/Type=Tramites.svg';
      case VerticalType.Parking:
        return '../../../assets/icons/gantt-icons/Type=Parking.svg';
      case VerticalType.ReplacementVehicle:
        return '../../../assets/icons/gantt-icons/Type=Vehiculo_sustitucion.svg';
      case VerticalType.TowTruck:
        return '../../../assets/icons/gantt-icons/Type=Grua.svg';
      case VerticalType.WhateverYouWant:
        return '../../../assets/icons/gantt-icons/Type=Pide_lo_que_necesites.svg';
      default:
        // En caso de un tipo no contemplado, devolvemos el icono genérico
        return '../../../assets/icons/gantt-icons/Type=Pide_lo_que_necesites.svg';
    }
  }

  private buildWeekSkeleton(): void {
    if (!this.weekStart) return;

    const start = dayjs(this.weekStart).startOf('day');
    this.days = Array.from({ length: 7 }).map((_, i) => {
      const d = start.add(i, 'day');
      return {
        date: d,
        label: d.format('dddd DD'),
      };
    });
    this.rows = [];
  }

  formatDriverTime(t?: DriverTime): string | undefined {
    if (t?.seconds) {
      return formatSecondsToHourAndMinutes(t?.seconds);
    }
    return;
  }

  private mapTasks(): void {
    const infoByGuid = new Map(
      this.data.driversData.map((d) => [d.driverGuid, d] as const)
    );

    const driverSet = new Set<string>();
    for (const day of this.data.ganttData) {
      for (const entry of day.dailyData.ganttData) driverSet.add(entry.key);
    }

    this.rows = [...driverSet].map((guid) => {
      const info = infoByGuid.get(guid);
      return {
        guid,
        displayName:
          info?.driverName ??
          (guid === EMPTY_GUID
            ? this.translate.instant('weekly-gantt.unassignedLabel')
            : guid),
        driverType: info?.driverType as DriverType | undefined,
        startHour: info?.startHour,
        endHour: info?.endHour,
        tasks: Array.from({ length: 7 }, () => [] as GanttData[]),
      } as DriverRow;
    });

    this.rows = [
      ...this.rows.filter((r) => r.guid !== EMPTY_GUID),
      ...this.rows.filter((r) => r.guid === EMPTY_GUID),
    ];

    const rowIndexByGuid = new Map(
      this.rows.map((r, idx) => [r.guid, idx] as const)
    );

    this.data.ganttData.forEach((dayBlock, dayIdx) => {
      dayBlock.dailyData.ganttData.forEach((entry: GanttDataEntry) => {
        const rIdx = rowIndexByGuid.get(entry.key);
        if (rIdx === undefined) return;
        entry.value.data
          .filter((g) => g.nodeType !== 'TRANSPORT_NODE')
          .forEach((g) => this.rows[rIdx].tasks[dayIdx].push(g));
      });
    });
  }

  getDriverTypeLabel(type?: DriverType): string {
    return this.driverTypeLabel[type ?? DriverType.UNKNOWN];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
